import { SingleSelectOption } from "@/components/controls/SingleSelect";
import { multiSelectionOptions } from "@/components/controls/MultiSelect";
import { ApolloCallOutcome, ApolloList } from "@/entities/apollo/types";

export const APOLLO_SETTINGS_TRANSLATION_PREFIX = "integrations.apolloSettings";

export type ListNameStatus = "found" | "missing" | "ambiguous";

// The list sync writes to a list by name and skips a name that matches no
// Apollo accounts list or more than one, so the picker shows which is which.
export const listNameOptions = (
  lists: ApolloList[],
  savedName: string,
  statusLabel: (status: Exclude<ListNameStatus, "found">) => string
): SingleSelectOption[] => {
  const counts = new Map<string, number>();
  lists.forEach(({ name }) => counts.set(name, (counts.get(name) ?? 0) + 1));

  const options: SingleSelectOption[] = Array.from(counts, ([name, count]) => ({
    id: name,
    displayName: name,
    subtitle: count > 1 ? statusLabel("ambiguous") : undefined
  }));
  if (savedName && !counts.has(savedName)) {
    options.unshift({
      id: savedName,
      displayName: savedName,
      subtitle: statusLabel("missing")
    });
  }
  return options;
};

// A saved id Apollo no longer returns stays selectable so it can be removed.
const withSavedValues = (
  options: multiSelectionOptions,
  savedValues: string[]
): multiSelectionOptions => {
  const known = new Set(options.map((option) => option.value));
  return [
    ...options,
    ...savedValues
      .filter((value) => !known.has(value))
      .map((value) => ({ label: value, value }))
  ];
};

export const listIdOptions = (
  lists: ApolloList[],
  savedIds: string[]
): multiSelectionOptions =>
  withSavedValues(
    lists.map(({ id, name }) => ({ label: name, value: id })),
    savedIds
  );

export const callOutcomeOptions = (
  outcomes: ApolloCallOutcome[],
  savedValues: string[]
): multiSelectionOptions =>
  withSavedValues(
    outcomes.map(({ value, displayName }) => ({ label: displayName, value })),
    savedValues
  );

export const sameValues = (
  nextValues: string[],
  savedValues: string[]
): boolean => {
  if (nextValues.length !== savedValues.length) return false;
  const nextValueSet = new Set(nextValues);
  return savedValues.every((value) => nextValueSet.has(value));
};
